import { cn } from '@reelplicate/ui/lib/utils';
import * as React from 'react';

/**
 * shadcn Table, restyled to the Figma Grid & Table page (38:166). Owned code:
 * the row / cell metrics are Figma's (header 40, body rows 56, 16px cell
 * padding, 1px border/subtle bottom stroke on every cell). Apps use the
 * wrappers in components/lib/table.tsx, which add sorting, selection and the
 * Figma cell types.
 *
 * The 1px bottom stroke lives on every cell, as in Figma where each Table Cell
 * draws its own INSIDE stroke: an inset box-shadow, so it takes no layout
 * space (a 56 row stays 56 with its content centred in the full height) and a
 * sticky header keeps it while the body scrolls beneath.
 */
function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<'table'> & { containerClassName?: string }) {
  return (
    <div
      data-slot="table-container"
      className={cn('relative w-full overflow-x-auto', containerClassName)}
    >
      <table
        data-slot="table"
        className={cn(
          'text-body-md text-text-primary w-full border-separate border-spacing-0',
          className,
        )}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return <thead data-slot="table-header" className={className} {...props} />;
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return <tbody data-slot="table-body" className={className} {...props} />;
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return <tfoot data-slot="table-footer" className={className} {...props} />;
}

/**
 * Figma Table Row: the fill is the row's (cells have none). Default
 * bg/surface; Hover bg/subtle; Selected (`data-selected`) brand/subtle.
 * Header rows (inside thead) are bg/subtle.
 */
function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'bg-bg-surface transition-colors duration-[120ms] ease-out',
        '[tbody>&]:hover:bg-bg-subtle [tbody>&]:data-selected:bg-brand-subtle',
        '[thead>&]:bg-bg-subtle',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Figma Table Header Cell: 40 tall, padding 0/16, Label/Small text/secondary
 * (text/primary once sorted), bg/subtle (on the cell too, so a sticky header
 * stays opaque) with a 1px border/subtle bottom stroke.
 */
function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'text-label-sm text-text-secondary bg-bg-subtle h-10 px-4 text-left align-middle whitespace-nowrap',
        'shadow-[inset_0_-1px_0_0_var(--color-border-subtle)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Figma Table Cell: 56 tall, padding 0/16, gap 8, Body/Medium text/primary,
 * 1px border/subtle bottom stroke (inside - the 56 includes it).
 */
function TableCell({ className, ...props }: React.ComponentProps<'td'>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        'h-14 px-4 align-middle whitespace-nowrap',
        'shadow-[inset_0_-1px_0_0_var(--color-border-subtle)]',
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return <caption data-slot="table-caption" className={className} {...props} />;
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
